import React from 'react';
import { Button, Spin, Table, Checkbox, Select, Modal } from 'antd';
import LookupField from '../../components/LookupField';
import { useAccessControlForm } from './hooks/useAccessControlForm';

const P0107Form: React.FC = () => {
  const {
    levels, subsystems,
    levelCode, levelName, levelValid,
    selectedSubsys, items, loading,
    copySource, copyTarget,
    setCopySource, setCopyTarget,
    handleLevelChange, handleLevelBlur, validateAndLoad,
    handleSubsysSelect,
    togglePermission, setAllPermissions,
    save, copyPermissions,
  } = useAccessControlForm();

  const handleCopy = () => {
    if (!copySource.trim() || !copyTarget.trim()) return;
    Modal.confirm({
      title: '確認複製',
      content: `確定要將等級「${copySource}」的使用權複製到等級「${copyTarget}」？`,
      okText: '確認',
      cancelText: '取消',
      onOk: copyPermissions,
    });
  };

  const columns = [
    {
      title: '程式代碼',
      dataIndex: 'f007_12',
      key: 'f007_12',
      width: 120,
      render: (val: string) => val.substring(2) || val,
    },
    {
      title: '程式名稱',
      dataIndex: 'program_name',
      key: 'program_name',
      width: 200,
    },
    {
      title: '類別',
      dataIndex: 'category',
      key: 'category',
      width: 80,
    },
    {
      title: '使用權',
      dataIndex: 'f007_15',
      key: 'f007_15',
      width: 80,
      render: (_: boolean, __: any, index: number) => (
        <Checkbox
          checked={items[index]?.f007_15}
          onChange={() => togglePermission(index)}
          disabled={!levelValid}
        />
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div className="max-w-4xl mx-auto p-6">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          使用權設定 (pw0107f1)
        </h2>

        {/* Level Code Input (BR-02, BR-03 DblClick→Lookup) */}
        <div className="flex items-center gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              使用等級:
            </label>
            <LookupField
              lookupType="level"
              value={levelCode}
              onChange={(code) => handleLevelChange(code.toUpperCase())}
              onCodeConfirm={(code) => validateAndLoad(code.toUpperCase())}
              maxLength={4}
              style={{ width: 160 }}
            />
          </div>
          <div className="pt-5">
            <span className={levelValid ? 'text-green-600' : 'text-red-500'}>
              {levelName || ''}
            </span>
          </div>
        </div>

        {/* Subsystem Select (BR-05) */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            子系統
          </label>
          <Select
            value={selectedSubsys || undefined}
            onChange={handleSubsysSelect}
            disabled={!levelValid}
            placeholder="選擇子系統"
            style={{ width: 300 }}
          >
            {subsystems.map(sub => (
              <Select.Option key={sub.f004_01} value={sub.f004_01}>
                {sub.f004_01} - {sub.f004_11}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Batch Buttons (BR-08, BR-09) */}
        <div className="flex items-center gap-2 mb-4">
          <Button
            onClick={() => setAllPermissions(true)}
            disabled={!levelValid || items.length === 0}
          >
            全部可用
          </Button>
          <Button
            onClick={() => setAllPermissions(false)}
            disabled={!levelValid || items.length === 0}
          >
            全部不准
          </Button>
          <Button
            type="primary"
            onClick={save}
            disabled={!levelValid || !selectedSubsys}
          >
            存檔
          </Button>
        </div>

        {/* Permission Grid (BR-06) */}
        <Table
          dataSource={items}
          columns={columns}
          rowKey="f007_12"
          pagination={false}
          size="small"
          scroll={{ y: 400 }}
          locale={{ emptyText: '請選擇使用等級和子系統' }}
        />

        {/* Copy Section (BR-14) */}
        <div className="mt-8 p-4 border rounded bg-gray-50">
          <h3 className="text-lg font-semibold mb-3">等級複製</h3>
          <div className="flex items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                目的等級
              </label>
              <Select
                value={copyTarget || undefined}
                onChange={setCopyTarget}
                placeholder="目的等級"
                style={{ width: 180 }}
                showSearch
                optionFilterProp="children"
              >
                {levels.map(lv => (
                  <Select.Option key={lv.f002_01} value={lv.f002_01}>
                    {lv.f002_01} - {lv.f002_11}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                來源等級
              </label>
              <Select
                value={copySource || undefined}
                onChange={setCopySource}
                placeholder="來源等級"
                style={{ width: 180 }}
                showSearch
                optionFilterProp="children"
              >
                {levels.map(lv => (
                  <Select.Option key={lv.f002_01} value={lv.f002_01}>
                    {lv.f002_01} - {lv.f002_11}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <Button
              onClick={handleCopy}
              disabled={!copySource || !copyTarget}
            >
              確認複製
            </Button>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default P0107Form;
